import { useEffect, useMemo, useState } from 'react';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faFileLines,
} from '@fortawesome/free-solid-svg-icons';
import Routes from 'constants/Routes';
import { CaseEventDTOWithUserDetails } from 'api/model';
import {
  getTableRowClassName,
  SavingRowAnimationWrapper,
} from 'components/Dashboard/table-helpers';
import Widget from 'components/Dashboard/Widget';
import { GreenPlatesWidgetsEnum, LongTitles, UPDATE_INTERVAL } from '../utils';
import {
  getHRGetMissingGreenPlatesUponStartOfAssignmentQueryKey,
  useHRGetMissingGreenPlatesUponStartOfAssignment,
  useHRMarkEventAsReadByHR,
} from 'api/hr/hr';
import { formatDateTime } from 'utils/date-helpers';
import GenericFileViewerModal from 'components/DetailedCase/EventsAndComments/EventAttachmentFileViewer';
import useModalStack from 'contexts/modal/useModalStack';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import TextButton from 'components/inputs/TextButton';
import ConfirmModal from 'components/Modal/ConfirmModal';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from 'styled-components';
import useTranslations from 'contexts/basicData/useTranslations';

const TABLE_NAME = 'missing-green-plates-list';

interface UserReports {
  userId: number;
  userName: string;
  reports: CaseEventDTOWithUserDetails[];
}

const MissingGreenPlatesWidget = () => {
  const [userReports, setUserReports] = useState<UserReports[]>([]);

  const [ongoingSaves, setOngoingSaves] = useState<number[]>([]);
  const {
    dashboardState: { searchText, selectedBusinessUnits },
  } = useDashboardControls();

  const modalStack = useModalStack();
  const theme = useTheme();
  const translations = useTranslations();

  const queryClient = useQueryClient();
  const { mutateAsync: markEventAsRead, status: markAsReadStatus } =
    useHRMarkEventAsReadByHR({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries([
            `${getHRGetMissingGreenPlatesUponStartOfAssignmentQueryKey()}`,
          ]);
        },
      },
    });

  const getMissingGreenPlateReports =
    useHRGetMissingGreenPlatesUponStartOfAssignment(
      {
        BusinessUnitIDs: selectedBusinessUnits,
      },
      {
        query: {
          refetchInterval: UPDATE_INTERVAL,
        },
      }
    );

  useEffect(() => {
    if (getMissingGreenPlateReports.data) {
      setUserReports(
        getMissingGreenPlateReports.data.reduce<UserReports[]>(
          (acc, report) => {
            const userId = report.assignedToID;
            const userName = report.assignedToUserName;

            const existingUser = acc.find((user) => user.userId === userId);
            if (existingUser) {
              existingUser.reports.push(report);
            } else if (userId && userName) {
              acc.push({ userId, userName, reports: [report] });
            }

            return acc;
          },
          []
        )
      );
    } else {
      setUserReports([] as UserReports[]);
    }
  }, [getMissingGreenPlateReports.data]);

  const filteredRows = useMemo(() => {
    if (!searchText.length) {
      return userReports;
    }
    return userReports
      ? userReports.filter((userReport) => {
          return (userReport.userName ?? '')
            .toLowerCase()
            .includes(searchText.toLowerCase());
        })
      : [];
  }, [userReports, searchText]);

  const handleConfirmedReportHandled = useMemo(
    () => async (report: CaseEventDTOWithUserDetails) => {
      setOngoingSaves([...ongoingSaves, report.assignedToID ?? -1]);

      await markEventAsRead({
        params: {
          caseEventId: report.id,
        },
      });
      modalStack.pop();
      modalStack.pop();
      setOngoingSaves(ongoingSaves.filter((id) => id !== report.assignedToID));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const columnSettings: ColumnSetting<UserReports, any>[] = useMemo(() => {
    const columns: ColumnSetting<UserReports, any>[] = [
      {
        head: 'Nr',
        render: (e, t, f, i) => i + 1,
        width: 15,
      },
      {
        head: 'Fälttestare',
        render: (row) => {
          return row.reports[0]?.assignedToUserName;
        },
        width: 100,
        sortFunction: (a, b) =>
          (a.reports[0]?.assignedToUserName ?? '').localeCompare(
            b.reports[0]?.assignedToUserName ?? ''
          ),
      },
      {
        head: 'Telefonnummer',
        render: (row) => {
          return row.reports[0]?.assignedToUserPhoneNumber;
        },
        width: 100,
        sortFunction: (a, b) =>
          (a.reports[0]?.assignedToUserPhoneNumber ?? '').localeCompare(
            b.reports[0]?.assignedToUserPhoneNumber ?? ''
          ),
      },
      {
        head: 'Senast rapporterad',
        render: (row) => {
          const latestReport = row.reports.reduce((prev, curr) => {
            return prev.created > curr.created ? prev : curr;
          });
          return formatDateTime(latestReport.created);
        },
        width: 100,
        sortFunction: (a, b) => {
          const aLatest = a.reports.reduce((prev, curr) => {
            return prev.created > curr.created ? prev : curr;
          });
          const bLatest = b.reports.reduce((prev, curr) => {
            return prev.created > curr.created ? prev : curr;
          });
          return aLatest.created.getTime() - bLatest.created.getTime();
        },
      },
      {
        head: 'Ohanterade rapporter',
        render: (row) => (
          <>
            {row.reports
              .sort((a, b) => {
                return b.created.getTime() - a.created.getTime(); // Latest first
              })
              .map((report) => (
                <FontAwesomeIcon
                  key={`Rapport-${report.id}`}
                  icon={faFileLines}
                  fontSize="18px"
                  color={theme.colors.foreground.tint}
                  style={{ cursor: 'pointer', marginRight: '5px' }}
                  title={`Rapporterat: ${formatDateTime(report.created)} \nRegNr: ${report.registrationNumber} \nÄrendetyp: ${translations.caseTypes[report.caseType]} \nUppdragsstatus: ${translations.assignmentStatuses[report.assignmentStatus]} \nUppdragstyp: ${report.assignmentType && translations.assignmentTypes[report.assignmentType]} \n\nRAPPORT \n${report.comment}`}
                  onClick={() => {
                    modalStack.push(
                      <GenericFileViewerModal
                        fileEnding={report.attachmentFileEnding}
                        fileGuid={report.attachmentFileGuid}
                        onClose={() => modalStack.pop()}
                        title={
                          <>
                            {report.registrationNumber}{' '}
                            <TextButton>
                              <FontAwesomeIcon
                                onClick={() => {
                                  window.open(
                                    `${Routes.search.index}/${report.caseID}`,
                                    report.caseID.toString() ?? '_blank'
                                  );
                                }}
                                icon={faArrowUpRightFromSquare}
                                fontSize="18px"
                                style={{ cursor: 'pointer' }}
                              />
                            </TextButton>
                          </>
                        }
                        description={report.comment}
                        buttons={[
                          {
                            label: 'Markera som hanterad',
                            onClick: () => {
                              modalStack.push(
                                <ConfirmModal
                                  title={` ${report.registrationNumber}`}
                                  content={`Är du säker på att du vill klarmarkera rapporten för ${report.registrationNumber}?`}
                                  confirmText="Klarmarkera"
                                  onCancel={() => modalStack.pop()}
                                  cancelText="Avbryt"
                                  onConfirm={async () => {
                                    await handleConfirmedReportHandled(report);
                                  }}
                                />
                              );
                            },
                          },
                        ]}
                      />
                    );
                  }}
                />
              ))}
          </>
        ),
        width: 100,
      },
    ];
    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Widget
      title={LongTitles[GreenPlatesWidgetsEnum.MissingGreenPlates]}
      count={userReports.length}
      isLoading={
        getMissingGreenPlateReports.isLoading || markAsReadStatus === 'loading'
      }
      info="Rapporterade grönskyltar som saknas eller inte är verifierade vid start av uppdrag."
    >
      <SavingRowAnimationWrapper
        tableName={TABLE_NAME}
        savingIds={ongoingSaves}
      >
        <Table
          columnSettings={columnSettings}
          rows={filteredRows}
          rowClassName={(row) => getTableRowClassName(TABLE_NAME, row.userId)}
          useColumnWidthAsFlexAmount
        />
      </SavingRowAnimationWrapper>
    </Widget>
  );
};

export default MissingGreenPlatesWidget;
